/**
 * Calendar (docs/features/calendar.md): the workspace's events, Google Calendar style — day / week / month views,
 * recurring events, attendees who are members (RSVP in the app) or outside guests (an .ics invite by email from the
 * organizer's mailbox; their Accept/Decline comes back as an iTIP REPLY through Mail), and an optional huddle to
 * join from the event. Events are visible to their organizer and attendees, plus everyone when `visibility` is
 * "workspace" (busy blocks only for others is not modelled yet).
 */
import { z } from "zod";
import { EmailAddress, Id, Timestamp } from "./entities.ts";

export const RsvpStatus = z.enum(["needs_action", "accepted", "declined", "tentative"]);
export type RsvpStatus = z.infer<typeof RsvpStatus>;

export const Attendee = z.object({
  /** A workspace member… */
  userId: Id.optional(),
  /** …or an outside guest (invited by email). */
  email: EmailAddress.optional(),
  name: z.string().max(200).optional(),
  response: RsvpStatus,
  /** Can be left out without blocking the event ("optional" in Google Calendar). */
  optional: z.boolean().optional(),
  /**
   * Their answers to single occurrences of a series (by original start) that differ from `response`. With `following`,
   * the answer holds from that occurrence on (a guest's "This and following events": like Google, the organizer's
   * series stays whole); an answer to a single occurrence still wins over it there. Core `occurrenceResponse` reads it.
   */
  occurrenceResponses: z.array(z.object({ at: Timestamp, response: RsvpStatus, following: z.boolean().optional() })).max(500).optional(),
});
export type Attendee = z.infer<typeof Attendee>;

/** One occurrence of a series changed on its own ("this event" in Google/Apple): `at` is its original start. */
export const OccurrenceOverride = z.object({
  at: Timestamp,
  start: Timestamp,
  end: Timestamp,
  title: z.string().min(1).max(500).optional(),
  location: z.string().max(500).optional(),
  description: z.string().max(20_000).optional(),
});
export type OccurrenceOverride = z.infer<typeof OccurrenceOverride>;

/**
 * A repeat rule (the subset every platform renders and the .ics carries as RRULE): every `interval` days / weeks
 * (on `weekdays`, 0 = Sunday) / months (same day of month) / years, until `until` or for `count` occurrences.
 */
export const Recurrence = z.object({
  freq: z.enum(["daily", "weekly", "monthly", "yearly"]),
  interval: z.number().int().min(1).max(99).default(1),
  weekdays: z.array(z.number().int().min(0).max(6)).max(7).optional(),
  until: Timestamp.optional(),
  count: z.number().int().min(1).max(1000).optional(),
  /** Occurrence starts removed from the series ("delete this event" on one occurrence). */
  exdates: z.array(Timestamp).max(500).optional(),
  /** Occurrences changed on their own (time, title, place, notes); the rest follow the series. */
  overrides: z.array(OccurrenceOverride).max(200).optional(),
});
export type Recurrence = z.infer<typeof Recurrence>;

export const CalendarEvent = z.object({
  id: Id,
  title: z.string().max(500),
  description: z.string().max(20_000).optional(),
  location: z.string().max(500).optional(),
  /** Start and end instants. All-day events run from local midnight to the next midnight(s) in `timeZone`. */
  start: Timestamp,
  end: Timestamp,
  allDay: z.boolean(),
  /** IANA zone the event was created in (all-day dates and recurrences follow it). */
  timeZone: z.string().max(64),
  recurrence: Recurrence.optional(),
  organizerId: Id,
  attendees: z.array(Attendee).max(300),
  /** "Join huddle": a huddle in this conversation (or a DM with the attendees) when it starts. */
  huddle: z.boolean(),
  /** The conversation the event belongs to (shown there, its huddle runs there). */
  channelId: Id.optional(),
  visibility: z.enum(["attendees", "workspace"]),
  /** Colour token name (`tag.<color>`). */
  color: z.string().max(20).optional(),
  /** My RSVP (absent when I'm the organizer or not invited). */
  myResponse: RsvpStatus.optional(),
  /** My answers to single occurrences (declining one meeting of a series), by original start; `following`: from it on. */
  myOccurrenceResponses: z.array(z.object({ at: Timestamp, response: RsvpStatus, following: z.boolean().optional() })).max(500).optional(),
  /** My reminders for single occurrences (by original start), instead of `reminderMinutes` there; null = none. */
  myOccurrenceReminders: z.array(z.object({ at: Timestamp, minutes: z.number().int().min(0).max(40_320).nullable() })).max(500).optional(),
  /**
   * Minutes before the start to remind me (my own setting; absent = no reminder). All-day events count back from their
   * local midnight on the wall clock (900 = the day before at 9:00). Defaults: core `defaultReminderMinutes`.
   */
  reminderMinutes: z.number().int().min(0).max(40_320).optional(),
  /** My reminders also come by email (Google Calendar's "Email" notification; my own setting). */
  reminderEmail: z.boolean().optional(),
  cancelled: z.boolean(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
  /** The iCalendar UID it has outside Bundle (Google's iCalUID, an emailed invite's UID): one meeting, one event. */
  icalUid: z.string().max(1024).optional(),
  /**
   * Someone outside the workspace organizes it (an emailed invite or an imported Google event): `organizerId` is the
   * member whose copy this is. Its time is theirs to change, and Bundle never emails its guests.
   */
  externalOrganizer: z.object({ email: EmailAddress, name: z.string().max(200).optional() }).optional(),
  /** The video call link (Google Meet, Zoom, Teams). */
  conferenceUrl: z.string().max(2000).optional(),
  /** Where it came from: "google" = imported from Google Calendar (Settings → Mail → Import); absent = made in Bundle. */
  source: z.enum(["google"]).optional(),
  /** One of the organizer's other calendars (`CalendarInfo`); absent = their main calendar. */
  calendarId: Id.optional(),
  /** "Show as free" (iCalendar TRANSP:TRANSPARENT): doesn't block booking pages. Absent = busy. */
  transparent: z.boolean().optional(),
  /** Booked through this booking page of the organizer's (booking.ts); the guest is the outside attendee. */
  bookingPageId: Id.optional(),
});
export type CalendarEvent = z.infer<typeof CalendarEvent>;

/** One of my other calendars (a secondary Google calendar I own, imported with my events). */
export const CalendarInfo = z.object({
  id: Id,
  name: z.string().max(200),
  /** Colour token name (`tag.<color>`). */
  color: z.string().max(20).optional(),
  /** "google" = imported from Google Calendar ("from Google"). */
  source: z.enum(["google"]).optional(),
});
export type CalendarInfo = z.infer<typeof CalendarInfo>;

/**
 * GET /v1/w/:ws/calendar/calendars — my other calendars (my main one isn't listed), and whether my Google Calendar
 * is still syncing into Bundle (`googleSync`: the workspace's Google import has Calendar on and I'm in it).
 */
export const ListCalendarsResponse = z.object({ calendars: z.array(CalendarInfo), googleSync: z.boolean() });
export type ListCalendarsResponse = z.infer<typeof ListCalendarsResponse>;

/**
 * One occurrence in a range (recurring events expand to many; `start`/`end` are the occurrence's). An occurrence
 * changed on its own, or one I answered on its own, carries `at` (its original start: the id to edit or answer it by)
 * and what differs from the series.
 */
export const EventOccurrence = z.object({
  eventId: Id,
  start: Timestamp,
  end: Timestamp,
  at: Timestamp.optional(),
  title: z.string().optional(),
  location: z.string().optional(),
  description: z.string().optional(),
  myResponse: RsvpStatus.optional(),
});
export type EventOccurrence = z.infer<typeof EventOccurrence>;

/** GET /v1/w/:ws/calendar/events?from&to — events overlapping [from, to) and their occurrences there. */
export const ListEventsQuery = z.object({ from: z.coerce.number().int(), to: z.coerce.number().int() });
export type ListEventsQuery = z.infer<typeof ListEventsQuery>;

export const ListEventsResponse = z.object({ events: z.array(CalendarEvent), occurrences: z.array(EventOccurrence) });
export type ListEventsResponse = z.infer<typeof ListEventsResponse>;

/** GET /v1/w/:ws/calendar/sync?cursor — for the phone apps' device calendar and CalDAV. "" = from the start. */
export const SyncEventsQuery = z.object({ cursor: z.string().max(80).optional() });
export type SyncEventsQuery = z.infer<typeof SyncEventsQuery>;

/**
 * My events (organizer or invited) changed since the cursor, whole series rather than occurrences; cancelled ones
 * come with `cancelled` so copies get deleted. `removed`: events I lost (taken off the guests); delete them first.
 * Ask again with `cursor` while `more`; keep the last cursor for next time.
 */
export const SyncEventsResponse = z.object({ events: z.array(CalendarEvent), removed: z.array(Id), cursor: z.string(), more: z.boolean() });
export type SyncEventsResponse = z.infer<typeof SyncEventsResponse>;

/** GET/POST /v1/w/:ws/calendar/feed(/reset) — my secret subscription link (read-only .ics for any calendar app). */
export const CalendarFeedResponse = z.object({ url: z.string(), webcalUrl: z.string() });
export type CalendarFeedResponse = z.infer<typeof CalendarFeedResponse>;

/**
 * An app password (like Google's): one per device or app, for IMAP, SMTP, CalDAV and CardDAV. Adding one never stops the
 * others; each is revoked on its own. Only its hash is kept, so the password itself is shown once, when it's made.
 * `label`: the name it was given (null for passwords made before they had names). `usedAt`: last sign-in with it
 * (to the minute), null if never.
 */
export const AppPassword = z.object({ id: Id, label: z.string().nullable(), createdAt: z.number().int(), usedAt: z.number().int().nullable() });
export type AppPassword = z.infer<typeof AppPassword>;

/** GET /v1/w/:ws/app-passwords — mine in this workspace, newest first. */
export const ListAppPasswordsResponse = z.object({ appPasswords: z.array(AppPassword) });
export type ListAppPasswordsResponse = z.infer<typeof ListAppPasswordsResponse>;

/**
 * POST /v1/w/:ws/app-passwords (and /calendar/dav-password): a new app password, named `label` (a device or app, e.g.
 * "Mac — Apple Calendar"). Without a label it's named for its `purpose` ("Mail app", "Calendar app") or "App password".
 */
export const CreateAppPasswordRequest = z.object({ label: z.string().trim().min(1).max(80).optional(), purpose: z.enum(["mail", "calendar"]).optional() });
export type CreateAppPasswordRequest = z.infer<typeof CreateAppPasswordRequest>;

/**
 * POST /v1/w/:ws/calendar/dav-password or /app-passwords — a new app password, shown once: add a CalDAV/CardDAV or
 * IMAP/SMTP account with this server, user name and password. The others keep working. DELETE
 * /calendar/dav-password revokes all of mine in this workspace; DELETE /app-passwords/:appPasswordId revokes one.
 */
export const CalDavPasswordResponse = z.object({ server: z.string(), username: z.string(), password: z.string(), appPassword: AppPassword });
export type CalDavPasswordResponse = z.infer<typeof CalDavPasswordResponse>;

/**
 * POST /v1/w/:ws/mail/apple-profile — a new app password (the others keep working) built into a profile that sets up
 * Mail, Calendar and Contacts on an iPhone or Mac: `profile` is the .mobileconfig, base64, signed (CMS) when the server
 * has its signing certificate (`signed`), so the device shows it as verified. Body: optional CreateAppPasswordRequest
 * (`label` names the password; default "Apple profile").
 */
export const MailProfileResponse = z.object({ filename: z.string(), profile: z.string(), signed: z.boolean(), appPassword: AppPassword });
export type MailProfileResponse = z.infer<typeof MailProfileResponse>;

/** Attendees as a request gives them: members by id, guests by email. */
const AttendeeInputs = z.array(z.object({ userId: Id.optional(), email: EmailAddress.optional(), name: z.string().max(200).optional(), optional: z.boolean().optional() })).max(300);

const EventFields = {
  title: z.string().min(1).max(500),
  description: z.string().max(20_000).optional(),
  location: z.string().max(500).optional(),
  start: Timestamp,
  end: Timestamp,
  allDay: z.boolean().default(false),
  timeZone: z.string().min(1).max(64),
  recurrence: Recurrence.optional(),
  /** Members by id, guests by email. */
  attendees: AttendeeInputs.default([]),
  huddle: z.boolean().default(false),
  channelId: Id.optional(),
  visibility: z.enum(["attendees", "workspace"]).default("attendees"),
  color: z.string().max(20).optional(),
  /** "Show as free". */
  transparent: z.boolean().optional(),
};

/** POST /v1/w/:ws/calendar/events — guests get an .ics invite from `fromMailboxId` (one of my mailboxes) when given. */
export const CreateEventRequest = z.object({ ...EventFields, reminderMinutes: z.number().int().min(0).max(40_320).optional(), reminderEmail: z.boolean().optional(), fromMailboxId: Id.optional() });
export type CreateEventRequest = z.infer<typeof CreateEventRequest>;

/** PATCH /v1/w/:ws/calendar/events/:eventId (organizer) — guests get an updated invite. */
export const UpdateEventRequest = z.object({
  title: EventFields.title.optional(),
  description: z.string().max(20_000).nullable().optional(),
  location: z.string().max(500).nullable().optional(),
  start: Timestamp.optional(),
  end: Timestamp.optional(),
  allDay: z.boolean().optional(),
  timeZone: z.string().min(1).max(64).optional(),
  recurrence: Recurrence.nullable().optional(),
  /** Absent = unchanged (no default here: a PATCH without attendees keeps them). */
  attendees: AttendeeInputs.optional(),
  huddle: z.boolean().optional(),
  visibility: z.enum(["attendees", "workspace"]).optional(),
  color: z.string().max(20).nullable().optional(),
  transparent: z.boolean().optional(),
  fromMailboxId: Id.optional(),
  /**
   * "This and following events": the occurrence (original start) to split a repeating event at. The series ends
   * before it, and a new series with this change starts there (`EventResponse.event`; the shortened one is `split`).
   */
  following: Timestamp.optional(),
});
export type UpdateEventRequest = z.infer<typeof UpdateEventRequest>;

/**
 * POST /v1/w/:ws/calendar/events/:eventId/rsvp — my answer (and my reminder: `reminderMinutes`, null = none;
 * `reminderEmail`). With `occurrence` (an occurrence's original start), for that occurrence only, and `reminderMinutes`
 * is my reminder for it alone. With `occurrence` and `following`, my answer to it and every later one ("This and
 * following events"): like Google, a guest's answer never changes the organizer's event, so the series stays whole and
 * the answer is kept as mine from that occurrence on (`myOccurrenceResponses` with `following`, replacing my answers
 * to later occurrences); answering from the first occurrence is answering the whole series.
 */
export const RsvpRequest = z.object({
  response: RsvpStatus,
  reminderMinutes: z.number().int().min(0).max(40_320).nullable().optional(),
  reminderEmail: z.boolean().optional(),
  occurrence: Timestamp.optional(),
  following: z.boolean().optional(),
});
export type RsvpRequest = z.infer<typeof RsvpRequest>;

/** `split`: after a "this and following" change, the original series (now ending before it); `event` is the new one. */
export const EventResponse = z.object({ event: CalendarEvent, split: CalendarEvent.optional() });
export type EventResponse = z.infer<typeof EventResponse>;

// ---------------------------------------------------------------------------------------------------------------
// Replacing Google Meet with a Bundle meeting (docs/features/calendar-meet-swap.md). A swapped event's
// `conferenceUrl` is its Bundle meeting link (`<origin>/m/abc-defg-hij`; core `conferenceKind` → "bundle").

/** The one-time prompt: not shown yet ("ask"), answered, or "Not now" (gone on every device either way). */
export const MeetPromptState = z.enum(["ask", "answered", "dismissed"]);
export type MeetPromptState = z.infer<typeof MeetPromptState>;

/** Settings → Huddles "Video calls on calendar events": each person chooses, or Bundle meetings for everyone. */
export const CalendarMeetingPolicy = z.enum(["member", "bundle"]);
export type CalendarMeetingPolicy = z.infer<typeof CalendarMeetingPolicy>;

/** Why an event was left out of a run. `google_scope`: "An admin needs to let Bundle edit calendars". */
export const MeetSwapSkipReason = z.enum(["google_scope", "google_error", "not_organizer", "no_meet", "ended", "gone", "no_mailbox", "mail_error"]);
export type MeetSwapSkipReason = z.infer<typeof MeetSwapSkipReason>;

/**
 * GET /v1/w/:ws/calendar/meet-swap/candidates — my upcoming events with a Meet link that I organise (`count`; whole
 * series count once), how many outside guests they have, the prompt's state, the workspace policy, and how many of them
 * wait for Google's permission (`googleScope`: Google events that can't be changed until an admin grants the scope).
 */
export const MeetSwapCandidatesResponse = z.object({
  count: z.number().int(),
  guests: z.number().int(),
  prompt: MeetPromptState,
  policy: CalendarMeetingPolicy,
  googleScope: z.number().int(),
  /** My `calendar.bundleMeetingDefault` ("Always use Bundle meetings for new events I organise"). */
  bundleMeetingDefault: z.boolean(),
  /** A run of mine still going (one at a time). */
  activeRunId: Id.optional(),
});
export type MeetSwapCandidatesResponse = z.infer<typeof MeetSwapCandidatesResponse>;

/**
 * POST /v1/w/:ws/calendar/meet-swap — "Just this event" (`scope: "event"`, the whole series) or "All my upcoming
 * events". `notify`: email guests (Google: sendUpdates=all|none). `always` sets `bundleMeetingDefault` and answers the
 * prompt. Starts after a 10-second hold (the toast's Undo = cancel). `fromMailboxId`: which of my mailboxes sends the
 * updated invites (default: my first one that can send).
 */
export const StartMeetSwapRequest = z.object({
  scope: z.enum(["event", "upcoming"]),
  eventId: Id.optional(),
  notify: z.boolean(),
  always: z.boolean().optional(),
  fromMailboxId: Id.optional(),
});
export type StartMeetSwapRequest = z.infer<typeof StartMeetSwapRequest>;

export const MeetSwapRunResponseId = z.object({ runId: Id });
export type MeetSwapRunResponseId = z.infer<typeof MeetSwapRunResponseId>;

/** POST /v1/w/:ws/calendar/meet-swap/restore — put the Google Meet links of a run back (within 30 days). */
export const RestoreMeetSwapRequest = z.object({ runId: Id, notify: z.boolean().optional() });
export type RestoreMeetSwapRequest = z.infer<typeof RestoreMeetSwapRequest>;

/** POST /v1/w/:ws/calendar/meet-swap/prompt — "Not now". */
export const MeetPromptRequest = z.object({ answer: z.literal("dismissed") });
export type MeetPromptRequest = z.infer<typeof MeetPromptRequest>;

export const MeetSwapRunStatus = z.enum(["held", "running", "paused", "done", "cancelled"]);
export type MeetSwapRunStatus = z.infer<typeof MeetSwapRunStatus>;

/** GET /v1/w/:ws/calendar/meet-swap/runs/:runId — a run's progress and what it left out. */
export const MeetSwapRun = z.object({
  id: Id,
  userId: Id,
  kind: z.enum(["swap", "restore"]),
  scope: z.enum(["event", "upcoming"]),
  status: MeetSwapRunStatus,
  notify: z.boolean(),
  total: z.number().int(),
  done: z.number().int(),
  skipped: z.number().int(),
  failed: z.number().int(),
  /** Work starts at this time (the Undo hold). */
  startsAt: Timestamp,
  createdAt: Timestamp,
  /** Restorable until (30 days after it ran; swap runs only). */
  restoreUntil: Timestamp.optional(),
  skippedEvents: z.array(z.object({ eventId: Id, title: z.string(), reason: MeetSwapSkipReason })).max(500),
  /** Started by the workspace policy (MeetPolicyRun). */
  policyRunId: Id.optional(),
});
export type MeetSwapRun = z.infer<typeof MeetSwapRun>;

export const MeetSwapRunResponse = z.object({ run: MeetSwapRun });
export type MeetSwapRunResponse = z.infer<typeof MeetSwapRunResponse>;

/**
 * GET/PUT /v1/w/:ws/calendar/settings — my calendar settings: `bundleMeetingDefault` (also used by booking pages),
 * the prompt's state, and the workspace policy (read-only here: "bundle" = locked on, "Turned on for <workspace> by an
 * admin").
 */
export const CalendarSettings = z.object({ bundleMeetingDefault: z.boolean(), meetPrompt: MeetPromptState, meetingPolicy: CalendarMeetingPolicy });
export type CalendarSettings = z.infer<typeof CalendarSettings>;
export const CalendarSettingsResponse = z.object({ settings: CalendarSettings });
export type CalendarSettingsResponse = z.infer<typeof CalendarSettingsResponse>;
export const UpdateCalendarSettingsRequest = z.object({ bundleMeetingDefault: z.boolean().optional() });
export type UpdateCalendarSettingsRequest = z.infer<typeof UpdateCalendarSettingsRequest>;

/** GET /v1/w/:ws/settings/calendar-meetings — the workspace policy (`calendar.meetingPolicy`) and its latest run. */
export const CalendarMeetingsSettingsResponse = z.object({ policy: CalendarMeetingPolicy, runId: Id.optional() });
export type CalendarMeetingsSettingsResponse = z.infer<typeof CalendarMeetingsSettingsResponse>;

/**
 * PUT /v1/w/:ws/settings/calendar-meetings (owners/admins). Turning "bundle" on with `moveExisting` starts a policy
 * run (every member's upcoming Meet events); `restore` with policy "member" puts the Google Meet links back for
 * everyone (the latest policy run, 30 days). Turning it off restores nothing by itself.
 */
export const UpdateCalendarMeetingsRequest = z.object({ policy: CalendarMeetingPolicy, moveExisting: z.boolean().default(true), notify: z.boolean().default(true), restore: z.boolean().optional() });
export type UpdateCalendarMeetingsRequest = z.infer<typeof UpdateCalendarMeetingsRequest>;
export const UpdateCalendarMeetingsResponse = z.object({ policy: CalendarMeetingPolicy, runId: Id.optional() });
export type UpdateCalendarMeetingsResponse = z.infer<typeof UpdateCalendarMeetingsResponse>;

/** GET /v1/w/:ws/calendar/meet-policy/runs/:runId — a policy run and each member's run. */
export const MeetPolicyRun = z.object({
  id: Id,
  kind: z.enum(["swap", "restore"]),
  status: MeetSwapRunStatus,
  notify: z.boolean(),
  createdAt: Timestamp,
  members: z.array(z.object({ userId: Id, runId: Id.optional(), status: MeetSwapRunStatus, total: z.number().int(), done: z.number().int(), skipped: z.number().int(), failed: z.number().int() })),
});
export type MeetPolicyRun = z.infer<typeof MeetPolicyRun>;
export const MeetPolicyRunResponse = z.object({ run: MeetPolicyRun });
export type MeetPolicyRunResponse = z.infer<typeof MeetPolicyRunResponse>;
